import { bucketLabel, type Bucket } from '../lib/time.ts'
import { formatNumber, formatSigned, niceTicks, niceTicksRange, pickVolumeUnit, type VolumeUnit } from './format.ts'
import { areaPath, barPath, el, h, linePath } from './svg.ts'

export interface ChartSeries {
  key: string
  label: string
  /** Categorical slot 1-8; colour follows the entity, never its rank. */
  slot: number
  values: number[]
}

export interface ChartOptions {
  bucketStarts: number[]
  bucket: Bucket
  series: ChartSeries[]
  /** Unit suffix for the value axis. Volume charts pass 'L'. */
  unit?: VolumeUnit
  /** Label the final point of each series directly (<= 4 series). */
  endLabels?: boolean
  height?: number
  /** Called with the raw value for a tooltip row; defaults to the axis format. */
  formatValue?: (v: number) => string
  /** For a diverging chart: what a value above / below zero means. */
  polarity?: { above: string; below: string }
}

const M = { top: 16, right: 16, bottom: 30, left: 58 }
const BAR_GAP = 2 // the surface gap — white does the separating
const MAX_BAR = 24
const MIN_W = 320

function color(slot: number): string {
  if (slot === -1) return 'var(--diverge-pos)'
  if (slot === -2) return 'var(--diverge-neg)'
  return `var(--series-${((slot - 1) % 8) + 1})`
}

interface Frame {
  svg: SVGSVGElement
  plot: SVGGElement
  w: number
  h: number
  x: (i: number) => number
  band: number
  y: (v: number) => number
  max: number
  unit: VolumeUnit
  fmt: (v: number) => string
}

function frame(opts: ChartOptions, maxValue: number, viewW: number, minValue = 0): Frame {
  const height = opts.height ?? 260
  const w = viewW - M.left - M.right
  const hgt = height - M.top - M.bottom
  const unit = opts.unit ?? pickVolumeUnit(Math.max(maxValue, Math.abs(minValue)))
  const fmt = opts.formatValue ?? ((v: number) => formatNumber(v * unit.scale, unit.decimals))

  const ticks =
    minValue < 0 ? niceTicksRange(minValue * unit.scale, maxValue * unit.scale) : niceTicks(maxValue * unit.scale)
  const axisMax = (ticks[ticks.length - 1] ?? 1) / unit.scale
  const axisMin = (ticks[0] ?? 0) / unit.scale
  const step = (ticks[1] ?? 1) - (ticks[0] ?? 0)
  const tickDecimals = Number.isInteger(step) ? 0 : Math.min(2, unit.decimals || 1)
  const range = axisMax - axisMin || 1
  const y = (v: number) => M.top + hgt - ((v - axisMin) / range) * hgt
  const n = Math.max(1, opts.bucketStarts.length)
  const band = w / n
  const x = (i: number) => M.left + band * i

  // Drawn at the host's real pixel width, so text is never scaled: a
  // stretched viewBox squashes every axis label on a narrow screen.
  const svg = el('svg', {
    viewBox: `0 0 ${viewW} ${height}`,
    width: viewW,
    height,
    class: 'chart-svg',
    role: 'img',
  })

  // Gridlines: hairline, solid, recessive — never dashed.
  const grid = el('g', { class: 'grid' })
  for (const t of ticks) {
    const yy = y(t / unit.scale)
    grid.append(el('line', { x1: M.left, x2: M.left + w, y1: yy, y2: yy, class: t === 0 ? 'axis-base' : 'gridline' }))
    grid.append(
      el(
        'text',
        { x: M.left - 8, y: yy + 4, class: 'axis-tick', 'text-anchor': 'end' },
        minValue < 0 ? formatSigned(t, tickDecimals) : formatNumber(t, tickDecimals),
      ),
    )
  }
  svg.append(grid)

  // X labels, thinned so they never collide.
  const every = Math.max(1, Math.ceil(opts.bucketStarts.length / Math.max(3, Math.floor(w / 72))))
  const xlab = el('g', { class: 'x-axis' })
  opts.bucketStarts.forEach((b, i) => {
    if (i % every !== 0 && i !== opts.bucketStarts.length - 1) return
    xlab.append(
      el(
        'text',
        { x: x(i) + band / 2, y: M.top + hgt + 18, class: 'axis-tick', 'text-anchor': 'middle' },
        bucketLabel(b, opts.bucket),
      ),
    )
  })
  svg.append(xlab)

  const plot = el('g', { class: 'plot' })
  svg.append(plot)
  return { svg, plot, w, h: hgt, x, band, y, max: axisMax, unit, fmt }
}

// --- tooltip ---------------------------------------------------------------

interface TooltipRow {
  slot: number
  label: string
  value: string
}

function makeTooltip(host: HTMLElement) {
  const tip = h('div', { class: 'chart-tooltip', hidden: true })
  host.append(tip)
  return {
    show(clientX: number, clientY: number, title: string, rows: TooltipRow[]) {
      tip.replaceChildren(
        h('div', { class: 'tt-title', text: title }),
        ...rows.map((r) =>
          h(
            'div',
            { class: 'tt-row' },
            h('span', { class: 'tt-dot', style: `background:${color(r.slot)}` }),
            h('span', { class: 'tt-label', text: r.label }),
            h('span', { class: 'tt-value', text: r.value }),
          ),
        ),
      )
      const box = host.getBoundingClientRect()
      tip.hidden = false
      const tw = tip.offsetWidth
      const left = Math.min(Math.max(clientX - box.left + 14, 4), box.width - tw - 4)
      tip.style.left = `${left}px`
      tip.style.top = `${Math.max(4, clientY - box.top - 12)}px`
    },
    hide() {
      tip.hidden = true
    },
  }
}

// --- cumulative area -------------------------------------------------------

/**
 * Running total per series over the period. Area wash at 10%, 2px line,
 * an end marker with a surface ring, and a crosshair tooltip — an HTML chart
 * is interactive, so the hover layer ships by default.
 */
export function renderCumulative(opts: ChartOptions): HTMLElement {
  return responsive((w) => drawCumulative(opts, w))
}

/**
 * Re-draws the chart at the host's measured width. The host is not in the
 * document when the chart is built, so the first draw waits for layout.
 */
function responsive(draw: (width: number) => HTMLElement): HTMLElement {
  const host = h('div', { class: 'chart-host' })
  let last = 0
  const ro = new ResizeObserver((entries) => {
    const w = Math.max(MIN_W, Math.floor(entries[0]?.contentRect.width ?? 0))
    if (!w || Math.abs(w - last) < 8) return
    last = w
    host.replaceChildren(draw(w))
  })
  ro.observe(host)
  return host
}

const finite = (vs: number[]) => vs.filter((v) => Number.isFinite(v))

function drawCumulative(opts: ChartOptions, viewW: number): HTMLElement {
  const host = h('div', { class: 'chart' })
  const maxValue = Math.max(1e-9, ...opts.series.flatMap((s) => finite(s.values)))
  const f = frame(opts, maxValue, viewW)
  const tip = makeTooltip(host)

  const n = opts.bucketStarts.length
  // Points sit at bucket centres so the curve lines up with the bars below it.
  const px = (i: number) => f.x(i) + f.band / 2

  for (const s of opts.series) {
    const pts = s.values.map((v, i) => [px(i), f.y(v)] as [number, number])
    if (pts.length === 1) pts.unshift([f.x(0), f.y(s.values[0]!)])
    f.plot.append(el('path', { d: areaPath(pts, f.y(0)), fill: color(s.slot), 'fill-opacity': 0.1, stroke: 'none' }))
    f.plot.append(
      el('path', { d: linePath(pts), fill: 'none', stroke: color(s.slot), 'stroke-width': 2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }),
    )
    const last = pts[pts.length - 1]
    if (last) {
      f.plot.append(el('circle', { cx: last[0], cy: last[1], r: 4.5, fill: color(s.slot), stroke: 'var(--surface-1)', 'stroke-width': 2 }))
    }
  }

  // Crosshair + hit strip.
  const cross = el('line', { class: 'crosshair', y1: M.top, y2: M.top + f.h, x1: 0, x2: 0, opacity: 0 })
  f.plot.append(cross)
  const overlay = el('rect', { x: M.left, y: M.top, width: f.w, height: f.h, fill: 'transparent', class: 'hit' })
  f.plot.append(overlay)

  const dots = opts.series.map((s) =>
    el('circle', { r: 4.5, fill: color(s.slot), stroke: 'var(--surface-1)', 'stroke-width': 2, opacity: 0 }),
  )
  dots.forEach((d) => f.plot.append(d))

  const onMove = (ev: PointerEvent) => {
    const box = f.svg.getBoundingClientRect()
    const rel = ((ev.clientX - box.left) / box.width) * viewW
    const i = Math.max(0, Math.min(n - 1, Math.round((rel - M.left - f.band / 2) / f.band)))
    const cx = px(i)
    cross.setAttribute('x1', String(cx))
    cross.setAttribute('x2', String(cx))
    cross.setAttribute('opacity', '1')
    opts.series.forEach((s, k) => {
      const d = dots[k]!
      d.setAttribute('cx', String(cx))
      d.setAttribute('cy', String(f.y(s.values[i] ?? 0)))
      d.setAttribute('opacity', '1')
    })
    tip.show(ev.clientX, ev.clientY, bucketLabel(opts.bucketStarts[i]!, opts.bucket), opts.series.map((s) => ({
      slot: s.slot,
      label: s.label,
      value: `${f.fmt(s.values[i] ?? 0)} ${f.unit.suffix}`,
    })))
  }
  const onLeave = () => {
    cross.setAttribute('opacity', '0')
    dots.forEach((d) => d.setAttribute('opacity', '0'))
    tip.hide()
  }
  f.svg.addEventListener('pointermove', onMove)
  f.svg.addEventListener('pointerleave', onLeave)

  host.append(f.svg)
  if (opts.endLabels !== false && opts.series.length <= 4) {
    host.append(endLabelRow(opts.series, f))
  }
  return host
}

/**
 * Direct end-labels live in HTML beneath the plot rather than inside it:
 * converging cumulative curves put their end-points on top of each other, and
 * nudging SVG labels apart detaches them from their lines. Each entry carries
 * the series key, so this row is also the chart's legend — callers must not
 * add a second one.
 */
function endLabelRow(series: ChartSeries[], f: Frame): HTMLElement {
  return h(
    'div',
    { class: 'end-labels' },
    ...series.map((s) =>
      h(
        'div',
        { class: 'end-label' },
        h('span', { class: 'key-line', style: `background:${color(s.slot)}` }),
        h('span', { class: 'end-label-name', text: s.label }),
        h('span', { class: 'end-label-value', text: `${f.fmt(s.values[s.values.length - 1] ?? 0)} ${f.unit.suffix}` }),
      ),
    ),
  )
}

// --- grouped bars ----------------------------------------------------------

/** Per-bucket bars, grouped by series, with a per-mark hover tooltip. */
export function renderBars(opts: ChartOptions): HTMLElement {
  return responsive((w) => drawBars(opts, w))
}

function drawBars(opts: ChartOptions, viewW: number): HTMLElement {
  const host = h('div', { class: 'chart' })
  const maxValue = Math.max(1e-9, ...opts.series.flatMap((s) => finite(s.values)))
  const f = frame(opts, maxValue, viewW)
  const tip = makeTooltip(host)

  const groups = opts.series.length
  const inner = Math.max(2, f.band - BAR_GAP * 2)
  const barW = Math.min(MAX_BAR, Math.max(1.5, (inner - BAR_GAP * (groups - 1)) / groups))
  const groupW = barW * groups + BAR_GAP * (groups - 1)
  const baseline = f.y(0)

  opts.bucketStarts.forEach((b, i) => {
    const left = f.x(i) + (f.band - groupW) / 2
    opts.series.forEach((s, k) => {
      const v = s.values[i] ?? 0
      const bx = left + k * (barW + BAR_GAP)
      if (v > 0) {
        const top = f.y(v)
        f.plot.append(
          el('path', { d: barPath(bx, top, barW, baseline - top, 4), fill: color(s.slot), class: 'bar' }),
        )
      }
      // The hit target is the whole band height, not the drawn bar — a 1px
      // column of data must still be hoverable.
      const hit = el('rect', { x: bx - BAR_GAP / 2, y: M.top, width: barW + BAR_GAP, height: f.h, fill: 'transparent', class: 'hit' })
      hit.addEventListener('pointerenter', (ev) => {
        tip.show(
          (ev as PointerEvent).clientX,
          (ev as PointerEvent).clientY,
          bucketLabel(b, opts.bucket),
          opts.series.map((ss) => {
            const v = ss.values[i]
            return {
              slot: ss.slot,
              label: ss.label,
              value: v === undefined || Number.isNaN(v) ? '—' : `${f.fmt(v)} ${f.unit.suffix}`,
            }
          }),
        )
      })
      hit.addEventListener('pointerleave', () => tip.hide())
      f.plot.append(hit)
    })
  })

  host.append(f.svg)
  return host
}

// --- diverging bars --------------------------------------------------------

/**
 * One signed series around a zero baseline. Colour carries the sign — the
 * palette's diverging pair, blue above and red below — and the caption
 * beneath says what each direction means, so polarity is never colour alone.
 */
export function renderDiverging(opts: ChartOptions): HTMLElement {
  return responsive((w) => drawDiverging(opts, w))
}

function drawDiverging(opts: ChartOptions, viewW: number): HTMLElement {
  const host = h('div', { class: 'chart' })
  const s = opts.series[0]
  if (!s) return host
  const vals = finite(s.values)
  const maxValue = Math.max(1e-9, ...vals)
  const minValue = Math.min(0, ...vals)
  const f = frame(opts, maxValue, viewW, minValue)
  const tip = makeTooltip(host)

  const barW = Math.min(MAX_BAR, Math.max(1.5, f.band - BAR_GAP * 2))
  const zero = f.y(0)

  opts.bucketStarts.forEach((b, i) => {
    const v = s.values[i]
    const bx = f.x(i) + (f.band - barW) / 2
    if (v !== undefined && Number.isFinite(v) && v !== 0) {
      const top = Math.min(zero, f.y(v))
      const bottom = Math.max(zero, f.y(v))
      // The rounded end is the data end: up for a positive bar, down for a
      // negative one — drawn mirrored so the flat side always meets zero.
      const d = v > 0 ? barPath(bx, top, barW, bottom - top, 4) : barPathDown(bx, top, barW, bottom - top, 4)
      f.plot.append(el('path', { d, fill: v > 0 ? 'var(--diverge-pos)' : 'var(--diverge-neg)', class: 'bar' }))
    }
    const hit = el('rect', { x: bx - BAR_GAP, y: M.top, width: barW + BAR_GAP * 2, height: f.h, fill: 'transparent', class: 'hit' })
    hit.addEventListener('pointerenter', (ev) => {
      const val = v === undefined || Number.isNaN(v) ? '—' : `${formatSigned(v * f.unit.scale, f.unit.decimals)} ${f.unit.suffix}`
      tip.show((ev as PointerEvent).clientX, (ev as PointerEvent).clientY, bucketLabel(b, opts.bucket), [
        { slot: v !== undefined && v < 0 ? -2 : -1, label: s.label, value: val },
      ])
    })
    hit.addEventListener('pointerleave', () => tip.hide())
    f.plot.append(hit)
  })

  host.append(f.svg)
  if (opts.polarity) {
    host.append(
      h(
        'div',
        { class: 'legend' },
        h('div', { class: 'legend-item' }, h('span', { class: 'key-line', style: 'background:var(--diverge-pos)' }), h('span', { text: `above zero: ${opts.polarity.above}` })),
        h('div', { class: 'legend-item' }, h('span', { class: 'key-line', style: 'background:var(--diverge-neg)' }), h('span', { text: `below zero: ${opts.polarity.below}` })),
      ),
    )
  }
  return host
}

/** A bar hanging below the baseline: square at the top (zero), rounded at the bottom. */
function barPathDown(x: number, y: number, w: number, hgt: number, r: number): string {
  const radius = Math.max(0, Math.min(r, w / 2, hgt))
  const bottom = y + hgt
  return (
    `M ${x} ${y}` +
    ` L ${x + w} ${y}` +
    ` L ${x + w} ${bottom - radius}` +
    ` Q ${x + w} ${bottom} ${x + w - radius} ${bottom}` +
    ` L ${x + radius} ${bottom}` +
    ` Q ${x} ${bottom} ${x} ${bottom - radius} Z`
  )
}

/** Legend — always present for two or more series; identity is never colour alone. */
export function renderLegend(series: ChartSeries[]): HTMLElement | null {
  if (series.length < 2) return null
  return h(
    'div',
    { class: 'legend' },
    ...series.map((s) =>
      h(
        'div',
        { class: 'legend-item' },
        h('span', { class: 'key-line', style: `background:${color(s.slot)}` }),
        h('span', { text: s.label }),
      ),
    ),
  )
}

export { color as seriesColor }
